import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { logout } from '../auth';

// --- Theme Context (Simple version for button capability) ---
// Note: In a larger app, this context would be lifted higher, but for this component, 
// we assume the parent RootApp manages the state and passes toggle function or we use a custom hook.
// Since we are refactoring RootApp to hold state, we will accept props here.

export default function GlobalHeader({ auth, setAuth, theme, toggleTheme }) {
    const navigate = useNavigate();
    const location = useLocation();
    const isHome = location.pathname === '/';
    const showBack = !isHome && window.history.length > 1;

    return (
        <nav className="fixed w-full z-50 flex items-center justify-between px-6 py-4 glass transition-all duration-300">
            <div className="flex items-center gap-4">
                {/* Back Button */}
                {showBack && (
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 rounded-full hover:bg-white/10 dark:hover:bg-white/10 hover:bg-black/5 text-muted hover:text-main transition-colors duration-200"
                        title="Go Back"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </button>
                )}

                <Link to="/" className="flex items-center gap-3.5 group">
                    <span className="text-2xl font-display font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400 group-hover:to-white transition-all duration-300 drop-shadow-sm">
                        RoadWatch
                    </span>
                </Link>
            </div>

            <div className="flex items-center gap-4">
                {/* Theme Toggle */}
                <button
                    onClick={toggleTheme}
                    className="p-2.5 rounded-xl bg-dark-800/50 dark:bg-dark-800/50 bg-gray-100 border border-white/5 dark:border-white/5 border-black/5 text-muted hover:text-main hover:bg-dark-800 dark:hover:bg-dark-800 hover:bg-gray-200 transition-all shadow-sm hover:shadow-md active:scale-95"
                    title={theme === 'dark' ? "Switch to B&W Theme" : "Switch to Dark Theme"}
                >
                    {theme === 'dark' ? (
                        /* Sun/Light Icon for B/W mode switch */
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                    ) : (
                        /* Moon/Dark Icon for Color mode switch */
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
                    )}
                </button>

                {auth ? (
                    <>
                        <span className="hidden md:inline text-sm font-medium text-muted">{auth.email}</span>
                        <button
                            onClick={() => { logout(); setAuth(null); window.location.href = '/'; }}
                            className="flex items-center gap-2 text-sm px-4 py-2 bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white border border-red-500/20 rounded-xl transition-all duration-300 font-semibold shadow-sm hover:shadow-red-500/30"
                        >
                            <span>Logout</span>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                        </button>
                    </>
                ) : (
                    <div className="flex gap-3">
                        <Link to="/login" className="text-sm font-semibold px-5 py-2.5 text-muted hover:text-main hover:bg-white/5 rounded-xl transition-all duration-300">Login</Link>
                        <Link to="/register" className="text-sm font-bold px-5 py-2.5 bg-primary-600 hover:bg-primary-500 text-white rounded-xl shadow-lg shadow-primary-600/30 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-primary-600/40">Get Started</Link>
                    </div>
                )}
            </div>
        </nav>
    );
}
